import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils/helpers';
import { useSysadminStore, useAuthStore } from '../../stores';

export const CompanySelector: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { companies, selectedCompanyId, selectedCompany, mode, selectCompany, fetchCompanies, isLoadingCompanies } =
    useSysadminStore();
  const { setBusiness } = useAuthStore();

  useEffect(() => {
    if (companies.length === 0 && !isLoadingCompanies) {
      fetchCompanies();
    }
  }, [companies.length, isLoadingCompanies, fetchCompanies]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (companyId: string | null) => {
    selectCompany(companyId);
    setIsOpen(false);
    setSearch('');

    if (companyId === null) {
      setBusiness(null as any);
      navigate('/admin/users-roles');
    } else {
      const company = companies.find((c) => c.id === companyId);
      if (company) {
        setBusiness({
          id: company.id,
          name: company.name,
          logo: company.logo,
          address: '',
          city: '',
          state: '',
          zipCode: '',
          phone: '',
          industry: '',
          createdAt: '',
          updatedAt: '',
        });
      }
      navigate('/dashboard');
    }
  };

  const displayName = mode === 'system' ? 'System Administrator' : selectedCompany?.name || 'Select Company';

  return (
    <div ref={dropdownRef} className="relative w-full">
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center w-full px-3 py-2 rounded-lg text-left transition-all duration-200',
          'bg-secondary-50 dark:bg-secondary-800 hover:bg-secondary-100 dark:hover:bg-secondary-700',
          'border border-secondary-200 dark:border-secondary-600'
        )}
      >
        <div className="flex items-center flex-1 min-w-0">
          {mode === 'system' ? (
            <ShieldCheckIcon className="w-4 h-4 mr-2 text-primary-500 flex-shrink-0" />
          ) : (
            <BuildingOffice2Icon className="w-4 h-4 mr-2 text-secondary-500 dark:text-secondary-400 flex-shrink-0" />
          )}
          <span className="text-sm font-medium text-secondary-900 dark:text-secondary-50 truncate">
            {displayName}
          </span>
        </div>
        <ChevronUpDownIcon className="w-4 h-4 text-secondary-400 flex-shrink-0 ml-1" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-elevated overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-secondary-200 dark:border-secondary-700">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search companies..."
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-600 rounded-md text-secondary-900 dark:text-secondary-50 placeholder-secondary-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto scrollbar-thin">
            {/* System Administrator option */}
            <button
              onClick={() => handleSelect(null)}
              className={cn(
                'flex items-center w-full px-3 py-2.5 text-sm transition-colors',
                selectedCompanyId === null
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                  : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
              )}
            >
              <ShieldCheckIcon className="w-4 h-4 mr-2 text-primary-500 flex-shrink-0" />
              <span className="flex-1 text-left font-medium">System Administrator</span>
              {selectedCompanyId === null && (
                <CheckIcon className="w-4 h-4 text-primary-500 flex-shrink-0" />
              )}
            </button>

            {/* Divider */}
            <div className="border-t border-secondary-200 dark:border-secondary-700" />

            {/* Companies */}
            {isLoadingCompanies ? (
              <div className="px-3 py-4 text-center text-sm text-secondary-500">Loading companies...</div>
            ) : filteredCompanies.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-secondary-500">No companies found</div>
            ) : (
              filteredCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelect(company.id)}
                  className={cn(
                    'flex items-center w-full px-3 py-2.5 text-sm transition-colors',
                    selectedCompanyId === company.id
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                  )}
                >
                  <BuildingOffice2Icon className="w-4 h-4 mr-2 text-secondary-400 flex-shrink-0" />
                  <span className="flex-1 text-left truncate">{company.name}</span>
                  {selectedCompanyId === company.id && (
                    <CheckIcon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
